/**
 * Per-node stretch measures miss a value that returns under a sibling in the same x-window.
 * Columns are parent blocks, not the value's own placements (a facing overlaps almost any span).
 */
import { slacksOf } from "../domain/bayShare";

export interface ColumnPlanItem {
  id: string;
  run: number;
  shelf: number;
  x: number;
  w: number;
  hf: number;
  path: readonly (string | null)[];
}

interface PlanLike {
  all: () => ColumnPlanItem[];
  /** The plan's own bay, raw units. */
  bayW?: number;
  nodeKey: (item: ColumnPlanItem, depth: number) => string;
}

interface PlanHelpers {
  right: (item: ColumnPlanItem) => number;
  width: (item: ColumnPlanItem) => number;
}

export interface ColumnBreak {
  value: string;
  depth: number;
  run: number;
  strayShelves: readonly number[];
  strayIds: readonly string[];
  homeShelves: readonly number[];
  homeIds: readonly string[];
  homeNode: string;
  strayNode: string;
  columnNodes: readonly string[];
  between: readonly string[];
}

const COLUMN_OVERLAP = 0.5;
/** Nested sizes/variants occupy a slice and are supposed to repeat under each band. */
const BAND_WIDTH_FRACTION = 0.6;
/** Edge abutment is normal; a real bite means the wall is patchwork, not a column. */
const FOREIGN_BITE = 0.2;

interface Span {
  x0: number;
  x1: number;
}

interface Block {
  node: string;
  run: number;
  items: ColumnPlanItem[];
  span: Span;
}

interface Band {
  shelves: number[];
  ids: string[];
  items: ColumnPlanItem[];
  width: number;
}

const spanOf = (items: readonly ColumnPlanItem[], helpers: PlanHelpers): Span => ({
  x0: Math.min(...items.map((item) => item.x)),
  x1: Math.max(...items.map((item) => helpers.right(item))),
});

const overlapFraction = (a: Span, b: Span): number => {
  const shared = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
  if (shared <= 0) return 0;
  const narrower = Math.min(a.x1 - a.x0, b.x1 - b.x0);
  return narrower > 0 ? shared / narrower : 0;
};

const bandsOf = (items: readonly ColumnPlanItem[], helpers: PlanHelpers): Band[] => {
  const byShelf = new Map<number, ColumnPlanItem[]>();
  for (const item of items) {
    const list = byShelf.get(item.shelf) ?? [];
    list.push(item);
    byShelf.set(item.shelf, list);
  }
  const bands: Band[] = [];
  let current: Band | null = null;
  for (const shelf of [...byShelf.keys()].sort((a, b) => a - b)) {
    const onShelf = byShelf.get(shelf) ?? [];
    const width = onShelf.reduce((sum, item) => sum + helpers.width(item), 0);
    if (current !== null && shelf === current.shelves[current.shelves.length - 1] + 1) {
      current.shelves.push(shelf);
      current.ids.push(...onShelf.map((item) => item.id));
      current.items.push(...onShelf);
      current.width += width;
    } else {
      current = {
        shelves: [shelf],
        ids: onShelf.map((item) => item.id),
        items: [...onShelf],
        width,
      };
      bands.push(current);
    }
  }
  return bands;
};

export const columnValueBreaks = (
  plan: PlanLike,
  depth: number,
  helpers: PlanHelpers,
): ColumnBreak[] => {
  const EPSILON = slacksOf(plan.bayW).fill;
  const all = plan.all().filter((item) => {
    const value = item.path[depth];
    return value !== null && value !== undefined && value !== "";
  });
  if (!all.length) return [];

  const byNode = new Map<string, ColumnPlanItem[]>();
  for (const item of all) {
    const key = `${item.run} ${plan.nodeKey(item, depth)}`;
    const list = byNode.get(key) ?? [];
    list.push(item);
    byNode.set(key, list);
  }
  const blocks: Block[] = [];
  byNode.forEach((items, key) => {
    blocks.push({
      node: plan.nodeKey(items[0], depth),
      run: items[0].run,
      items,
      span: spanOf(items, helpers),
    });
  });

  const parent = blocks.map((_, index) => index);
  const find = (index: number): number => {
    let root = index;
    while (parent[root] !== root) root = parent[root];
    let walk = index;
    while (parent[walk] !== walk) {
      const next = parent[walk];
      parent[walk] = root;
      walk = next;
    }
    return root;
  };
  const union = (a: number, b: number) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent[rootB] = rootA;
  };
  for (let i = 0; i < blocks.length; i++) {
    for (let j = i + 1; j < blocks.length; j++) {
      if (blocks[i].run !== blocks[j].run) continue;
      if (overlapFraction(blocks[i].span, blocks[j].span) >= COLUMN_OVERLAP) union(i, j);
    }
  }
  const columns = new Map<number, Block[]>();
  blocks.forEach((block, index) => {
    const root = find(index);
    const list = columns.get(root) ?? [];
    list.push(block);
    columns.set(root, list);
  });

  const breaks: ColumnBreak[] = [];
  columns.forEach((column) => {
    if (new Set(column.map((block) => block.node)).size < 2) return;

    const items = column.flatMap((block) => block.items);
    const run = column[0].run;

    // Pairwise non-overlap invents columns from patchwork; a foreign bite means the shelf is shared.
    const columnWindow = spanOf(items, helpers);
    const windowWidth = columnWindow.x1 - columnWindow.x0;
    if (windowWidth <= 0) return;
    const memberIds = new Set(items.map((item) => item.id));
    const columnShelves = new Set(items.map((item) => item.shelf));
    const intruded = all.some((item) => {
      if (item.run !== run || memberIds.has(item.id) || !columnShelves.has(item.shelf))
        return false;
      const overlap =
        Math.min(columnWindow.x1, helpers.right(item)) - Math.max(columnWindow.x0, item.x);
      return overlap / windowWidth >= FOREIGN_BITE;
    });
    if (intruded) return;
    const nodeOf = new Map<string, string>();
    for (const item of items) nodeOf.set(item.id, plan.nodeKey(item, depth));

    const byValue = new Map<string, ColumnPlanItem[]>();
    for (const item of items) {
      const value = item.path[depth] as string;
      const list = byValue.get(value) ?? [];
      list.push(item);
      byValue.set(value, list);
    }

    const columnNodes = [...column]
      .sort(
        (a, b) =>
          Math.min(...a.items.map((i) => i.shelf)) - Math.min(...b.items.map((i) => i.shelf)),
      )
      .map((block) => block.node);

    const columnSpan = spanOf(items, helpers);
    const columnWidth = columnSpan.x1 - columnSpan.x0;
    const spansColumn = (band: Band): boolean => {
      if (columnWidth <= 0) return false;
      const span = spanOf(band.items, helpers);
      return (span.x1 - span.x0) / columnWidth >= BAND_WIDTH_FRACTION;
    };

    byValue.forEach((members, value) => {
      const bands = bandsOf(members, helpers).filter(spansColumn);
      if (bands.length < 2) return;
      const home = bands.reduce((best, band) =>
        band.width > best.width || (band.width === best.width && band.ids.length > best.ids.length)
          ? band
          : best,
      );
      for (const band of bands) {
        if (band === home) continue;

        const homeNodes = new Set(home.ids.map((id) => nodeOf.get(id)));
        const bandNodes = new Set(band.ids.map((id) => nodeOf.get(id)));
        if ([...bandNodes].some((key) => homeNodes.has(key))) continue;

        const upper = home.shelves[home.shelves.length - 1] < band.shelves[0] ? home : band;
        const lower = upper === home ? band : home;
        const betweenLo = upper.shelves[upper.shelves.length - 1] + 1;
        const betweenHi = lower.shelves[0] - 1;
        if (betweenHi < betweenLo) continue;

        const homeSpan = spanOf(home.items, helpers);
        const bandSpan = spanOf(band.items, helpers);
        const x0 = Math.min(homeSpan.x0, bandSpan.x0);
        const x1 = Math.max(homeSpan.x1, bandSpan.x1);
        const between = new Map<number, Set<string>>();
        for (const item of items) {
          if (item.shelf < betweenLo || item.shelf > betweenHi) continue;
          if (item.x >= x1 - EPSILON || helpers.right(item) <= x0 + EPSILON) continue;
          const other = item.path[depth] as string;
          if (other === value) continue;
          const set = between.get(item.shelf) ?? new Set<string>();
          set.add(other);
          between.set(item.shelf, set);
        }
        // Empty shelf between offset shelving is not a fracture.
        if (between.size === 0) continue;

        breaks.push({
          value,
          depth,
          run,
          strayShelves: [...band.shelves],
          strayIds: [...band.ids],
          homeShelves: [...home.shelves],
          homeIds: [...home.ids],
          homeNode: [...homeNodes][0] ?? "",
          strayNode: [...bandNodes][0] ?? "",
          columnNodes,
          between: [
            ...new Set(
              [...between.keys()]
                .sort((p, q) => p - q)
                .flatMap((shelf) => [...(between.get(shelf) ?? [])]),
            ),
          ],
        });
      }
    });
  });
  return breaks;
};
